import { Box, CircularProgress } from '@mui/material'
import { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/auth/authStore'
import { AppLayout } from '@/components/AppLayout'
import { TestingBanner } from '@/components/TestingBanner'
import { AvailabilityPage } from '@/pages/AvailabilityPage'
import { CustomersPage } from '@/pages/CustomersPage'
import { EnquiriesPage } from '@/pages/EnquiriesPage'
import { EnquiryDetailPage } from '@/pages/EnquiryDetailPage'
import { EnquirySlipPage } from '@/pages/EnquirySlipPage'
import { FeedbackPage } from '@/pages/FeedbackPage'
import { NotificationsPage } from '@/pages/NotificationsPage'
import { InvoicesPage } from '@/pages/InvoicesPage'
import { KitchenPage } from '@/pages/KitchenPage'
import { LoginPage } from '@/pages/LoginPage'
import { EbReadingsPage } from '@/pages/EbReadingsPage'
import { AuditLogPage } from '@/pages/AuditLogPage'
import { ReportsPage } from '@/pages/ReportsPage'
import { ScheduledReportsPage } from '@/pages/ScheduledReportsPage'
import { GatePassesPage } from '@/pages/GatePassesPage'
import { LogisticsPage } from '@/pages/LogisticsPage'
import { MyDeskPage } from '@/pages/MyDeskPage'
import { QuotationBuilderPage } from '@/pages/QuotationBuilderPage'
import { MenuPage } from '@/pages/MenuPage'
import { OrderDetailPage } from '@/pages/OrderDetailPage'
import { OrderDocumentAuditPage } from '@/pages/OrderDocumentAuditPage'
import { OrdersPage } from '@/pages/OrdersPage'
import { PaymentsPage } from '@/pages/PaymentsPage'
import { QuotationDetailPage } from '@/pages/QuotationDetailPage'
import { QuotationsPage } from '@/pages/QuotationsPage'
import { AccessPage } from '@/pages/AccessPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { StaffPage } from '@/pages/StaffPage'
import { ExecutiveDashboardPage } from '@/pages/dashboards/ExecutiveDashboardPage'
import { FinanceDashboardPage } from '@/pages/dashboards/FinanceDashboardPage'
import { OperationsDashboardPage } from '@/pages/dashboards/OperationsDashboardPage'
import { SalesDashboardPage } from '@/pages/dashboards/SalesDashboardPage'
import { FeedbackFormPage } from '@/portal/FeedbackFormPage'
import { OrderChangeApprovalPage } from '@/portal/OrderChangeApprovalPage'
import { QuotationAcceptPage } from '@/portal/QuotationAcceptPage'
import PublicTermsPage from '@/pages/PublicTermsPage'
import SharedDocumentPage from '@/pages/SharedDocumentPage'
import { ApiConfigurationPage } from '@/pages/ApiConfigurationPage'
import KitchenSlipPage from '@/pages/orders/KitchenSlipPage'
import { CustomerLoginPage } from '@/portal/CustomerLoginPage'
import { CustomerSignupPage } from '@/portal/CustomerSignupPage'
import { CustomerPasswordPage } from '@/portal/CustomerPasswordPage'
import { CustomerPortalPage } from '@/portal/CustomerPortalPage'
import { portalSession } from '@/portal/portalApi'

function RequireAuth({ children }: { children: JSX.Element }) {
  const status = useAuthStore((state) => state.status)
  const location = useLocation()

  if (status === 'unknown') {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location }} />
  }
  return children
}

/** Sends a customer who is not signed in back to the portal sign-in. */
function RequiresPortalSignIn({ children }: { children: React.ReactNode }) {
  if (!portalSession.signedIn()) {
    return <Navigate to="/my/login" replace />
  }
  return <>{children}</>
}

export default function App() {
  const restore = useAuthStore((state) => state.restore)

  useEffect(() => {
    void restore()
  }, [restore])

  return (
    <>
      {/* Above everything, including the sign-in page and the customer portal:
          somebody handed a link for testing should be told before they type a
          password, and a customer sent a stamped quotation should see why. */}
      <TestingBanner />
      <Routes>
      {/* Public customer links: the token in the URL is the credential. */}
      <Route path="/quotation/:token" element={<QuotationAcceptPage />} />
      <Route path="/feedback/:token" element={<FeedbackFormPage />} />
      {/* Any document the office sent: the link opens the file and nothing else. */}
      <Route path="/d/:token" element={<SharedDocumentPage />} />
      {/* Scanned off a printed document, by somebody who is not signed in. */}
      <Route path="/terms" element={<PublicTermsPage />} />
      {/* A change to an agreed order, answered by the customer on a link. */}
      <Route path="/order-change/:token" element={<OrderChangeApprovalPage />} />
      <Route path="/login" element={<LoginPage />} />
      {/* Outside the application shell: it is printed, and a navigation rail
          down the side of a sheet handed to a customer is not wanted on paper
          or on screen. */}
      <Route
        path="/enquiries/:id/slip"
        element={
          <RequireAuth>
            <EnquirySlipPage />
          </RequireAuth>
        }
      />
      {/* The kitchen's sheet, printed the same way and for the same reason:
          the dishes are named in Tamil. */}
      <Route
        path="/orders/:id/kitchen-slip"
        element={
          <RequireAuth>
            <KitchenSlipPage />
          </RequireAuth>
        }
      />

      {/* The customer portal. Its own sign-in, its own token, and no part of
          the staff routes: a customer never reaches a screen built for staff. */}
      <Route path="/my/login" element={<CustomerLoginPage />} />
      <Route path="/my/signup" element={<CustomerSignupPage />} />
      <Route path="/my/password" element={<RequiresPortalSignIn><CustomerPasswordPage /></RequiresPortalSignIn>} />
      <Route path="/my" element={<RequiresPortalSignIn><CustomerPortalPage /></RequiresPortalSignIn>} />

      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        {/* Every role holds order.read; only management holds report.executive,
            so the executive dashboard was a permission error for most users. */}
        <Route path="/" element={<Navigate to="/my-desk" replace />} />
        <Route path="/dashboard/executive" element={<ExecutiveDashboardPage />} />
        <Route path="/dashboard/sales" element={<SalesDashboardPage />} />
        <Route path="/dashboard/operations" element={<OperationsDashboardPage />} />
        <Route path="/dashboard/finance" element={<FinanceDashboardPage />} />

        <Route path="/enquiries" element={<EnquiriesPage />} />
        <Route path="/enquiries/:id" element={<EnquiryDetailPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/availability" element={<AvailabilityPage />} />
        <Route path="/my-desk" element={<MyDeskPage />} />
        <Route path="/quotations" element={<QuotationsPage />} />
        <Route path="/quotations/new" element={<QuotationBuilderPage />} />
        {/* The same wizard, loaded with what is on the current version and
            saved as a revision instead of a new quotation. */}
        <Route path="/quotations/:editId/edit" element={<QuotationBuilderPage />} />
        <Route path="/quotations/:id" element={<QuotationDetailPage />} />

        <Route path="/orders" element={<OrdersPage />} />
        {/* The same wizard, ending in an order instead of a quotation. */}
        <Route path="/orders/new" element={<QuotationBuilderPage asOrder />} />
        {/* And again for a confirmed order: loaded with its sessions and
            menu, saved wholesale - past the change-request desk, not
            through it. */}
        <Route path="/orders/:orderEditId/revise" element={<QuotationBuilderPage />} />
        {/* The confirmation, the standing proforma and the standing tax
            invoice, compared line by line - a drift between them caught
            by looking rather than by a customer noticing first. */}
        <Route path="/orders/:id/document-audit" element={<OrderDocumentAuditPage />} />
        <Route path="/orders/:id" element={<OrderDetailPage />} />
        <Route path="/kitchen" element={<KitchenPage />} />
        <Route path="/staff" element={<StaffPage />} />
        <Route path="/logistics" element={<LogisticsPage />} />
        <Route path="/gate-passes" element={<GatePassesPage />} />
        <Route path="/eb-readings" element={<EbReadingsPage />} />
        <Route path="/audit-log" element={<AuditLogPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/:slug" element={<ReportsPage />} />
        <Route path="/scheduled-reports" element={<ScheduledReportsPage />} />
        <Route path="/menu" element={<MenuPage />} />

        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/receipts" element={<PaymentsPage />} />
        {/* Where the module used to live. Bookmarks and older links still
            open it rather than falling through to Not found. */}
        <Route path="/payments" element={<Navigate to="/receipts" replace />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/api-configuration" element={<ApiConfigurationPage />} />
        <Route path="/access" element={<AccessPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}
